import type {
	API_CreateScheduleSessionRequest,
	API_CreateScheduleSessionResponse,
	API_ListScheduleSessionsWithRelationsResponse,
	API_RescheduleScheduleSessionResponse,
	API_UpdateScheduleSessionRequest,
	AttendanceFilters,
	EpochTimeStamp,
	FE_ScheduleSession,
} from "@pilatestime/shared-types";
import {
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import api from "@/api/api_client";
import { convertScheduleSessionToEventSession } from "@/utils/attendanceUtils";

// Query keys
export const scheduleSessionKeys = {
	all: ["scheduleSessions"] as const,
	lists: () => [...scheduleSessionKeys.all, "list"] as const,
	// Use cache key that includes normalized date range for efficient caching
	list: (
		organizationId: string,
		normalizedRange?: {
			startDate: Date;
			endDate: Date;
		} | null,
	) =>
		[...scheduleSessionKeys.lists(), organizationId, normalizedRange] as const,
	// New monthly cache keys
	monthly: () => [...scheduleSessionKeys.all, "monthly"] as const,
	month: (organizationId: string, monthKey: string) =>
		[...scheduleSessionKeys.monthly(), organizationId, monthKey] as const,
	details: () => [...scheduleSessionKeys.all, "detail"] as const,
	detail: (organizationId: string, sessionId: string) =>
		[...scheduleSessionKeys.details(), organizationId, sessionId] as const,
	bySchedule: (organizationId: string, scheduleId: string) =>
		[
			...scheduleSessionKeys.all,
			"bySchedule",
			organizationId,
			scheduleId,
		] as const,
};

// Helper to get month key in format "MM_YYYY"
function getMonthKey(date: Date): string {
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear().toString();
	return `${month}_${year}`;
}

// Helper to get month date range
function getMonthDateRange(
	year: number,
	month: number,
): {
	startDate: Date;
	endDate: Date;
} {
	// Convert 1-indexed month to 0-indexed for JavaScript Date constructor
	const startDate = new Date(year, month - 1, 1);
	startDate.setHours(0, 0, 0, 0);

	const endDate = new Date(year, month, 0); // Last day of month (month is already 1-indexed, so this works)
	endDate.setHours(23, 59, 59, 999);

	return {
		startDate: startDate,
		endDate: endDate,
	};
}

// Helper to parse month key back to year and month
function parseMonthKey(monthKey: string): { year: number; month: number } {
	const [monthStr, yearStr] = monthKey.split("_");
	return {
		year: Number.parseInt(yearStr, 10),
		month: Number.parseInt(monthStr, 10), // Keep as 1-indexed month (getMonthDateRange expects 1-indexed)
	};
}

// Fetch function for a specific month of data (used by useQueries)
async function fetchMonthSessions(
	organizationId: string,
	monthKey: string,
): Promise<FE_ScheduleSession[]> {
	const { year, month } = parseMonthKey(monthKey);
	const { startDate, endDate } = getMonthDateRange(year, month);

	const params = new URLSearchParams();
	params.append("startDate", startDate.toString());
	params.append("endDate", endDate.toString());

	const response = await api.get<API_ListScheduleSessionsWithRelationsResponse>(
		`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
	);

	// Convert to FE_ScheduleSession format
	return response.data.sessions.map((session) =>
		convertScheduleSessionToEventSession(session, session.attendanceCount),
	);
}

// Hook for attendance calendar - uses useQueries for proper reactivity
export function useAttendanceCalendarData(organizationId: string) {
	// Get current month key
	const currentMonthKey = useMemo(() => {
		const now = new Date();
		return getMonthKey(now);
	}, []);

	// Track which months have been loaded/requested
	const [loadedMonthKeys, setLoadedMonthKeys] = useState<string[]>(() => [
		currentMonthKey,
	]);

	// Create reactive queries for all loaded months using useQueries
	// This automatically re-renders when any query updates (including after invalidation)
	const monthQueries = useQueries({
		queries: loadedMonthKeys.map((monthKey) => ({
			queryKey: scheduleSessionKeys.month(organizationId, monthKey),
			queryFn: () => fetchMonthSessions(organizationId, monthKey),
			staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
			gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
			enabled: !!organizationId,
		})),
	});

	// Aggregate results from all month queries - this is reactive!
	// When any query updates (e.g., after invalidation), this recomputes automatically
	const allSessions = useMemo(() => {
		const sessions = monthQueries
			.filter((q) => q.status === "success" && q.data)
			.flatMap((q) => q.data as FE_ScheduleSession[]);

		// Sort sessions by date
		return sessions.sort(
			(a, b) =>
				new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
		);
	}, [monthQueries]);

	// Function to load a specific month - just adds to state, useQueries handles the rest
	const loadMonth = useCallback((date: Date): Promise<boolean> => {
		const monthKey = getMonthKey(date);

		setLoadedMonthKeys((prev) => {
			if (prev.includes(monthKey)) {
				return prev; // Already in the list
			}
			return [...prev, monthKey];
		});

		// Return true immediately - useQueries will handle the actual fetching
		return Promise.resolve(true);
	}, []);

	// Check if a specific month is loading
	const isMonthLoading = useCallback(
		(date: Date): boolean => {
			const monthKey = getMonthKey(date);
			const index = loadedMonthKeys.indexOf(monthKey);
			if (index === -1) return false;
			return monthQueries[index]?.isFetching ?? false;
		},
		[loadedMonthKeys, monthQueries],
	);

	// Derive loading and error states from all queries
	const isLoading = monthQueries.some((q) => q.isLoading);
	const error = monthQueries.find((q) => q.error)?.error ?? null;

	return {
		data: allSessions,
		isLoading,
		error,
		loadMonth,
		isMonthLoading,
	};
}

// Legacy hook for backward compatibility - simplified version
export function useScheduleSessions(
	organizationId: string,
	filters?: AttendanceFilters,
) {
	const {
		data: allSessions,
		isLoading,
		error,
	} = useAttendanceCalendarData(organizationId);

	// Apply filters to the cached data
	const filteredSessions = useMemo(() => {
		let filtered = allSessions;

		// Apply date filters
		if (filters?.startDate && filters?.endDate) {
			const startDate = filters.startDate;
			const endDate = filters.endDate;
			filtered = filtered.filter((session) => {
				const sessionDate = new Date(session.startTime).getTime();
				return sessionDate >= startDate && sessionDate <= endDate;
			});
		}

		// Apply other filters
		if (filters?.staffMemberId) {
			filtered = filtered.filter(
				(session: FE_ScheduleSession) =>
					session.staffMemberId === filters.staffMemberId,
			);
		}

		if (filters?.establishmentId) {
			filtered = filtered.filter(
				(session: FE_ScheduleSession) =>
					session.establishmentId === filters.establishmentId,
			);
		}

		if (filters?.scheduleType) {
			filtered = filtered.filter(
				(session: FE_ScheduleSession) => session.type === filters.scheduleType,
			);
		}

		if (filters?.status) {
			switch (filters.status) {
				case "active":
					filtered = filtered.filter(
						(session: FE_ScheduleSession) =>
							session.isActive && !session.isCanceled,
					);
					break;
				case "canceled":
					filtered = filtered.filter(
						(session: FE_ScheduleSession) => session.isCanceled,
					);
					break;
				// "all" shows everything, no filter needed
			}
		}

		return filtered;
	}, [allSessions, filters]);

	return {
		data: filteredSessions,
		isLoading,
		error,
	};
}

// Fetch schedule session by ID
export function useScheduleSession(organizationId: string, sessionId: string) {
	return useQuery({
		queryKey: scheduleSessionKeys.detail(organizationId, sessionId),
		queryFn: async (): Promise<FE_ScheduleSession> => {
			// Use the list endpoint with a filter to get the single session with relations and attendance
			const params = new URLSearchParams();
			params.append("sessionId", sessionId);

			const response =
				await api.get<API_ListScheduleSessionsWithRelationsResponse>(
					`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
				);

			// Find the specific session (should be the only one returned)
			const session = response.data.sessions.find((s) => s.id === sessionId);
			if (!session) {
				throw new Error(`Session ${sessionId} not found`);
			}

			return convertScheduleSessionToEventSession(
				session,
				session.attendanceCount,
			);
		},
		enabled: !!organizationId && !!sessionId,
	});
}

// Fetch schedule sessions by schedule ID
export function useScheduleSessionsBySchedule(
	organizationId: string,
	scheduleId: string,
) {
	return useQuery({
		queryKey: scheduleSessionKeys.bySchedule(organizationId, scheduleId),
		queryFn: async (): Promise<FE_ScheduleSession[]> => {
			// Use the optimized endpoint with schedule filter
			const params = new URLSearchParams();
			params.append("scheduleId", scheduleId);

			const response =
				await api.get<API_ListScheduleSessionsWithRelationsResponse>(
					`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
				);

			// Convert to FE_ScheduleSession format - attendance counts are now included
			const eventSessions = response.data.sessions.map((session) => {
				return convertScheduleSessionToEventSession(
					session,
					session.attendanceCount,
				);
			});

			return eventSessions;
		},
		enabled: !!organizationId && !!scheduleId,
	});
}

// Create schedule session
export function useCreateScheduleSession(organizationId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			data: API_CreateScheduleSessionRequest,
		): Promise<API_CreateScheduleSessionResponse> => {
			const response = await api.post<API_CreateScheduleSessionResponse>(
				`/organizations/${organizationId}/schedule-sessions`,
				data,
			);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Update schedule session
export function useUpdateScheduleSession(
	organizationId: string,
	sessionId: string,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (
			data: API_UpdateScheduleSessionRequest,
		): Promise<API_CreateScheduleSessionResponse> => {
			const response = await api.put<API_CreateScheduleSessionResponse>(
				`/organizations/${organizationId}/schedule-sessions/${sessionId}`,
				data,
			);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Delete schedule session
export function useDeleteScheduleSession(organizationId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (sessionId: string): Promise<void> => {
			await api.delete(
				`/organizations/${organizationId}/schedule-sessions/${sessionId}`,
			);
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Cancel schedule session
export function useCancelScheduleSession(organizationId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (sessionId: string): Promise<void> => {
			await api.patch(
				`/organizations/${organizationId}/schedule-sessions/${sessionId}/cancel`,
			);
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Reactivate schedule session
export function useReactivateScheduleSession(organizationId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (sessionId: string): Promise<void> => {
			await api.patch(
				`/organizations/${organizationId}/schedule-sessions/${sessionId}/reactivate`,
			);
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Reschedule schedule session
export function useRescheduleScheduleSession(organizationId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			sessionId,
			newSessionDate,
			newStartTime,
			newEndTime,
		}: {
			sessionId: string;
			newSessionDate: number;
			newStartTime: string;
			newEndTime: string;
		}): Promise<API_RescheduleScheduleSessionResponse> => {
			const response = await api.patch<API_RescheduleScheduleSessionResponse>(
				`/organizations/${organizationId}/schedule-sessions/${sessionId}/reschedule`,
				{
					newSessionDate,
					newStartTime,
					newEndTime,
				},
			);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate all schedule session queries (lists, monthly, details, bySchedule)
			queryClient.invalidateQueries({
				queryKey: scheduleSessionKeys.all,
			});
		},
	});
}

// Helper hook to get current date range for calendar
export function useCalendarDateRange() {
	const getCurrentWeekRange = (): {
		startDate: EpochTimeStamp;
		endDate: EpochTimeStamp;
	} => {
		const now = new Date();
		const startOfWeek = new Date(now);
		startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
		startOfWeek.setHours(0, 0, 0, 0);

		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
		endOfWeek.setHours(23, 59, 59, 999);

		return {
			startDate: startOfWeek.getTime(),
			endDate: endOfWeek.getTime(),
		};
	};

	const getCurrentMonthRange = (): {
		startDate: EpochTimeStamp;
		endDate: EpochTimeStamp;
	} => {
		const now = new Date();
		const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
		startOfMonth.setHours(0, 0, 0, 0);

		const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
		endOfMonth.setHours(23, 59, 59, 999);

		return {
			startDate: startOfMonth.getTime(),
			endDate: endOfMonth.getTime(),
		};
	};

	return {
		getCurrentWeekRange,
		getCurrentMonthRange,
	};
}
